<template>
  <v-dialog :model-value="modelValue" max-width="600" @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title>{{ expenseForm.id ? $t('expenses.edit') : $t('expenses.add') }}</v-card-title>
      <v-card-text>
        <v-form>
          <v-select
            v-model="expenseForm.wallet_id"
            :items="props.wallets"
            item-title="display_name"
            item-value="id"
            :label="$t('expenses.wallet')"
            required
            density="compact"
          />
          <v-select
            v-model="expenseForm.expenses_type_id"
            :items="props.expensesTypes"
            item-title="name"
            item-value="id"
            :label="$t('expenses.expenseType')"
            required
            density="compact"
            class="mt-2"
          />
          <v-select
            v-model="expenseForm.user_id"
            :items="props.users"
            item-title="email"
            item-value="id"
            :label="$t('expenses.user')"
            required
            density="compact"
            class="mt-2"
          />
          <v-row class="mt-2" no-gutters>
            <v-col cols="8" class="pr-1">
              <v-text-field
                v-model="expenseForm.amount"
                :label="$t('expenses.amount')"
                type="number"
                required
                density="compact"
                @update:model-value="() => calculateAmountUsdt()"
              />
            </v-col>
            <v-col cols="4" class="pl-1">
              <v-text-field
                v-model="expenseForm.amount_usdt"
                :label="$t('expenses.usdt')"
                type="number"
                required
                density="compact"
              />
            </v-col>
          </v-row>
          <v-text-field
            v-model="expenseForm.transaction_date"
            :label="$t('expenses.transactionDate')"
            type="date"
            required
            density="compact"
            class="mt-2"
          />
          <v-textarea
            v-model="expenseForm.description"
            :label="$t('expenses.description')"
            rows="3"
            density="compact"
            class="mt-2"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="handleCancel">{{ $t('common.cancel') }}</v-btn>
        <v-btn color="primary" @click="$emit('save', expenseForm)">{{ expenseForm.id ? $t('expenses.update') : $t('common.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { apiGet } from '../../utils/api'
import { getTodayString } from '../../utils/date'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Props {
  modelValue: boolean
  expense?: any
  wallets: any[]
  expensesTypes: any[]
  users: any[]
}

const props = withDefaults(defineProps<Props>(), {
  expense: null,
  wallets: () => [],
  expensesTypes: () => [],
  users: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'save': [expense: any]
  'cancel': []
}>()

const expenseForm = ref({
  id: null as number | null,
  wallet_id: null as number | null,
  expenses_type_id: null as number | null,
  user_id: null as number | null,
  amount: 0,
  amount_usdt: 0,
  description: '',
  transaction_date: ''
})

const fetchRateFromDB = async (coinId: number | null, created_at?: string | null) => {
  if (!coinId) return null
  
  try {
    let url = `/api/coin_rates/latest?coin_id=${coinId}`
    if (created_at) {
      url += `&date_before=${created_at}`
    }
    const response = await apiGet(url)
    if (response.ok) {
      const rateData = await response.json()
      return rateData.rate
    }
  } catch (error) {
    console.error('Fetch rate from DB error:', error)
  }
  return null
}

const calculateAmountUsdt = async () => {
  if (!expenseForm.value.wallet_id || !expenseForm.value.amount) {
    expenseForm.value.amount_usdt = 0
    return
  }
  
  const wallet = props.wallets.find(w => w.id === expenseForm.value.wallet_id)
  if (!wallet) {
    expenseForm.value.amount_usdt = 0
    return
  }
  
  // Try to get rate from database first
  let rate: number | null = null
  if (wallet.coin && wallet.coin.id) {
    const transactionDate = expenseForm.value.transaction_date
      ? new Date(expenseForm.value.transaction_date).toISOString()
      : null
    rate = await fetchRateFromDB(wallet.coin.id, transactionDate)
  }
  
  // Fallback to wallet's usdt_rate if DB fetch fails
  if (!rate && wallet.usdt_rate) {
    rate = parseFloat(wallet.usdt_rate.toString())
  }
  
  if (rate) {
    expenseForm.value.amount_usdt = parseFloat((expenseForm.value.amount * rate).toFixed(2))
  } else {
    expenseForm.value.amount_usdt = 0
  }
}

// Watch for wallet changes to fetch rate from database
watch(() => expenseForm.value.wallet_id, async (newWalletId) => {
  if (!newWalletId) return
  await calculateAmountUsdt()
})

// Watch for transaction date changes to refetch rate
watch(() => expenseForm.value.transaction_date, async (newDate) => {
  if (!newDate || !expenseForm.value.wallet_id) return
  await calculateAmountUsdt()
})

// Initialize form when expense prop changes
watch(() => props.expense, (newExpense) => {
  if (newExpense) {
    expenseForm.value = {
      id: newExpense.id,
      wallet_id: newExpense.wallet_id,
      expenses_type_id: newExpense.expenses_type_id,
      user_id: newExpense.user_id,
      amount: newExpense.amount || 0,
      amount_usdt: newExpense.amount_usdt || 0,
      description: newExpense.description || '',
      transaction_date: newExpense.transaction_date ? (typeof newExpense.transaction_date === 'string' && newExpense.transaction_date.includes('T') 
        ? new Date(newExpense.transaction_date).toISOString().split('T')[0]
        : newExpense.transaction_date) : getTodayString()
    }
  }
}, { immediate: true })

const handleCancel = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

watch(() => props.modelValue, async (isOpen) => {
  if (isOpen) {
    // Ensure form is initialized when dialog opens
    if (!expenseForm.value) {
      const currentUserId = props.users && props.users.length > 0 ? props.users[0].id : null
      expenseForm.value = {
        id: null,
        wallet_id: null,
        expenses_type_id: null,
        user_id: currentUserId,
        amount: 0,
        amount_usdt: 0,
        description: '',
        transaction_date: getTodayString()
      }
    }
    if (!props.expense) {
      // Set defaults when opening for a new expense
      const currentUserId = props.users && props.users.length > 0 ? props.users[0].id : null
      expenseForm.value = {
        id: null,
        wallet_id: null,
        expenses_type_id: null,
        user_id: currentUserId,
        amount: 0,
        amount_usdt: 0,
        description: '',
        transaction_date: getTodayString()
      }
    }
  } else if (!isOpen && !props.expense) {
    // Reset form when dialog closes and it's not an edit
    expenseForm.value = {
      id: null,
      wallet_id: null,
      expenses_type_id: null,
      user_id: null,
      amount: 0,
      amount_usdt: 0,
      description: '',
      transaction_date: ''
    }
  }
})
</script>

